import { MutableRefObject, useCallback, useMemo, useRef } from 'react';

interface UseInputCaptureArgs {
  onSelectImage: (file: File) => void;
  capture: 'user' | 'environment' | undefined;
  disabled: boolean | undefined;
}

interface UseInputCaptureRet {
  inputCaptureProps: {
    accept: string;
    capture: 'user' | 'environment';
    'data-cy': string;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    type: 'file';
    ref: MutableRefObject<HTMLInputElement | null>;
    disabled: boolean;
    style: {
      display: 'none';
    };
  };
  buttonProps: {
    onClick: () => void;
    disabled: boolean;
  };
}

export default function useInputCapture({
  onSelectImage,
  capture,
  disabled,
}: UseInputCaptureArgs): UseInputCaptureRet {
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      // FIXME: onSelectImage should allow for null
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      onSelectImage(file);
    },
    [onSelectImage],
  );

  const handleClick = useCallback(() => {
    // NOTE: We manually trigger a click because
    // using a label didn't work on older devices (like Moto X 2nd Gen)
    refInputCapture.current?.click();
  }, []);

  const refInputCapture = useRef<HTMLInputElement>(null);

  const inputCaptureProps = useMemo<UseInputCaptureRet['inputCaptureProps']>(
    () => ({
      accept: 'image/*' as const,
      capture: capture ?? 'environment',
      disabled: disabled ?? false,
      onChange: handleChange,
      ref: refInputCapture,
      style: {
        display: 'none' as const,
      },
      type: 'file',
      // Stub for Cypress
      'data-cy': 'file-upload',
    }),
    [capture, disabled, handleChange],
  );

  const buttonProps = useMemo<UseInputCaptureRet['buttonProps']>(
    () => ({
      disabled: disabled || refInputCapture == null,
      onClick: handleClick,
    }),
    [disabled, handleClick],
  );

  return {
    inputCaptureProps,
    buttonProps,
  };
}
